// Client API du Coffre-fort du Délibéré — endpoints /api/v1/deliberation/*.
// Accès arbitre/greffier (le backend renvoie 403 sinon ; géré côté page).
import { apiClient } from "@/lib/api";

const base = (dossierId: number) => `/api/v1/deliberation/${dossierId}`;

// --- Verrou -----------------------------------------------------------------
export interface LockStatus {
  lock_status: string;
  verrouille: boolean;
  date_verrou: string | null;
  sentence_rendue: boolean;
  jours_restants_sentence: number | null;
  escalade_requise: boolean;
}
export async function getLockStatus(d: number): Promise<LockStatus> {
  return (await apiClient.get<LockStatus>(`${base(d)}/lock-status`)).data;
}

// --- Sessions ---------------------------------------------------------------
export interface DeliberationSession {
  id: string;
  dossier_id: number;
  status: string;
  deliberation_mode: string;
  lock_status: string;
  lock_delay_hours: number;
  majority_reached: boolean;
  closed_instruction_at: string | null;
  started_at: string;
}
export async function listSessions(d: number): Promise<DeliberationSession[]> {
  return (await apiClient.get<DeliberationSession[]>(`${base(d)}/sessions`)).data ?? [];
}
export async function openSession(d: number, mode = "SOLO"): Promise<DeliberationSession> {
  return (await apiClient.post(`${base(d)}/sessions`, { deliberation_mode: mode })).data;
}
export async function closeInstruction(d: number, sessionId: string): Promise<DeliberationSession> {
  return (await apiClient.post(`${base(d)}/sessions/${sessionId}/close-instruction`, {})).data;
}

// --- Concordance ------------------------------------------------------------
export interface ConcordanceLine {
  id: string;
  chef_de_demande: string;
  traitement: string | null;
  is_validated: boolean;
  reason: string | null;
  motif_ecart: string | null;
}
export interface ConcordanceProgress {
  total: number;
  traites: number;
  ecartes: number;
  progress_pct: number;
  complete: boolean;
  lines: ConcordanceLine[];
}
export async function getConcordance(d: number): Promise<ConcordanceProgress> {
  return (await apiClient.get<ConcordanceProgress>(`${base(d)}/concordance`)).data;
}
export async function addConcordanceLine(d: number, chef: string): Promise<ConcordanceLine> {
  return (await apiClient.post(`${base(d)}/concordance`, { chef_de_demande: chef })).data;
}
export async function validateConcordanceLine(
  d: number,
  lineId: string,
  input: { is_validated: boolean; reason?: string; motif_ecart?: string },
): Promise<ConcordanceLine> {
  return (await apiClient.post(`${base(d)}/concordance/${lineId}/validate`, input)).data;
}

// --- Notes ------------------------------------------------------------------
export interface Note {
  id: string;
  confidentiality_level: string;
  content: string | null;
  created_at: string;
}
export async function listNotes(d: number): Promise<Note[]> {
  return (await apiClient.get<Note[]>(`${base(d)}/notes`)).data ?? [];
}
export async function createNote(d: number, content: string): Promise<Note> {
  return (await apiClient.post(`${base(d)}/notes`, { content, confidentiality_level: "DELIBERATORY" })).data;
}

// --- Versions de sentence + signatures (Bouton Rouge) -----------------------
export interface SentenceVersion {
  id: string;
  version_number: number;
  content_hash: string;
  is_final: boolean;
  change_summary: string | null;
  created_at: string;
}
export async function listSentenceVersions(d: number): Promise<SentenceVersion[]> {
  return (await apiClient.get<SentenceVersion[]>(`${base(d)}/sentence-versions`)).data ?? [];
}
export async function createSentenceVersion(
  d: number,
  content: Record<string, unknown>,
  changeSummary?: string,
): Promise<SentenceVersion> {
  return (await apiClient.post(`${base(d)}/sentence-versions`, { content, change_summary: changeSummary })).data;
}

export interface Signature {
  id: string;
  arbitrator_id: string;
  status: string;
  document_hash: string | null;
  confirmed_at: string | null;
}
export interface SignaturesStatus {
  requis: number;
  confirmees: number;
  signee: boolean;
  signatures: Signature[];
}
const sigBase = (d: number, v: string) => `${base(d)}/sentence-versions/${v}/sign`;
export async function getSignatures(d: number, v: string): Promise<SignaturesStatus> {
  return (await apiClient.get<SignaturesStatus>(`${base(d)}/sentence-versions/${v}/signatures`)).data;
}
export async function signArm(
  d: number, v: string,
  input: { responsibility_accepted: boolean; mfa_verified: boolean; reflection_delay_seconds?: number },
): Promise<Signature> {
  return (await apiClient.post(`${sigBase(d, v)}/arm`, input)).data;
}
export async function signConfirm(d: number, v: string): Promise<SignaturesStatus> {
  return (await apiClient.post(`${sigBase(d, v)}/confirm`, {})).data;
}
export async function signCancel(d: number, v: string): Promise<Signature> {
  return (await apiClient.post(`${sigBase(d, v)}/cancel`, {})).data;
}

// --- Consentement de délibération -------------------------------------------
export async function getConsentStatus(d: number): Promise<{ consent_donne: boolean }> {
  return (await apiClient.get(`${base(d)}/consent-status`)).data;
}
export async function postConsent(d: number): Promise<{ consent_donne: boolean }> {
  return (await apiClient.post(`${base(d)}/consent`, {})).data;
}

// --- Votes / opinions collégiales -------------------------------------------
export interface Vote {
  id: string;
  arbitrator_id: string;
  vote_type: string;
  opinion_text: string | null;
  created_at: string;
}
export async function listVotes(d: number): Promise<Vote[]> {
  return (await apiClient.get<Vote[]>(`${base(d)}/votes`)).data ?? [];
}
export async function postVote(
  d: number,
  voteType: string,
  opinionText?: string,
): Promise<Vote> {
  return (await apiClient.post(`${base(d)}/votes`, { vote_type: voteType, opinion_text: opinionText })).data;
}

// --- Export probant ---------------------------------------------------------
export interface SentenceExport {
  dossier_id: number;
  version_number: number;
  content: Record<string, unknown>;
  content_hash: string;
  is_final: boolean;
  zetes_timestamp_id: string | null;
  signee: boolean;
  signatures: Signature[];
}
export async function getSentenceExport(d: number, v: string): Promise<SentenceExport> {
  return (await apiClient.get<SentenceExport>(`${base(d)}/sentence-versions/${v}/export`)).data;
}

// --- Modèle de sentence multilingue (fr/en/pt) ------------------------------
export interface SentenceTemplate {
  langue: string;
  sections: { section: string; titre: string }[];
}
export async function getSentenceTemplate(langue: string): Promise<SentenceTemplate> {
  return (await apiClient.get<SentenceTemplate>(`/api/v1/deliberation/sentence-template?langue=${langue}`)).data;
}
